import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { EventManagerAdminService } from './event-manager/event-manager-admin.service';
import { KeyValAdminService } from './key-val-admin.service';
import { LoginService } from '@shared-angular/services/login.service';

@Injectable({
  providedIn: 'root'
})

export class PlayAudioService {
  public lastPlayedItem = { text: "", time: 0 };
  public currentPlayingHash: string = "";
  public currentLoadingHash: string = "";
  public isVoiceTester: boolean = false;
  private audioState: Subject<[string, string]> = new Subject<[string, string]>();
  public currentAudioStatus = this.audioState.asObservable();
  public state: string = "idle";
  private audioDict: Record<string, { status: string, audio: ArrayBuffer }> = {};
  private audio: HTMLAudioElement = new Audio();

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
    private keyValService: KeyValAdminService,
    private eventManager: EventManagerAdminService
  ) {
    this.audio.onended = () => {
      this.currentPlayingHash = "";
      this.audioState.next(["idle", ""]);
      this.state = "idle";
      this.eventManager.triggerEventHook("RAGAI.AUDIO.SPEECH.done", { text: this.lastPlayedItem });
    };
    this.audio.onpause = () => {
      this.currentPlayingHash = "";
      this.audioState.next(["idle", ""]);
      this.state = "idle";
    };
  }

  public anyAudioIsLoading() {
    return this.currentLoadingHash !== "";
  }
  public anyAudioIsPlaying() {
    return this.currentPlayingHash !== "";
  }
  /*
  * This function generates a hash from the given text
  */
  public getHashFromText(string: string) {
    let hash = 0;
    if (string.length == 0) return hash.toString();
    for (let i = 0; i < string.length; i++) {
      const char = string.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  }
  /*
  * This function saves the hash given text to the dict and returns the hash
  */
  public storeHashFromText(text: string) {
    const hash = this.getHashFromText(text);
    this.audioDict[hash] ??= { status: "created", audio: new ArrayBuffer(0) };
    return hash;
  }

  public compareHashes(text: string) {
    return this.getHashFromText(text);
  }

  public removeHashFromDict(hash: string) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.audioDict[hash];
  }

  public async playAudio(input: string, botId: string, voice: string | undefined) {
    if (this.lastPlayedItem.text === input && Date.now() - this.lastPlayedItem.time < 500) return;
    this.lastPlayedItem = { text: input, time: Date.now() };
    const hash = this.storeHashFromText(input);
    voice ??= (await this.keyValService.getKeyValByType("CONFIGURATION")).find((a) => a.key == "AUDIO_VOICE")?.value as string || undefined;
    if ((voice === undefined || voice === 'zufällig') && !this.isVoiceTester) {
      const res = await firstValueFrom(this.http.get<{ voice: string }>("/api/toolbox/audio/getvoice/" + botId));
      voice = res.voice;
      await this.keyValService.storeKeyValEntry("AUDIO_VOICE", voice, "CONFIGURATION");
    }
    this.eventManager.triggerEventHook("RAGAI.AUDIO.SAY.request", { text: input, voice });

    if (this.state === "play") {
      this.audio.pause();
      this.audioState.next(["idle", ""]);
      this.state = "idle";
    }

    this.audioState.next(["load", hash]);
    this.state = "load";

    if (this.audioDict[hash]?.status === "created") {
      this.currentLoadingHash = hash;
      const httpCall = this.http.post("/api/toolbox/audio/create", { input, botId, userId: this.loginService.loggedInUser?._id, voice }, { responseType: 'arraybuffer' });
      const res = await firstValueFrom(httpCall);
      this.currentLoadingHash = "";
      this.audioDict[hash].audio = res;
      this.audioDict[hash].status = "loaded";

    }
    if (this.state !== "idle") {
      this.audioState.next(["play", hash]);
      this.state = "play";
      const blobPart = this.audioDict[hash]?.audio;
      if (!blobPart) return;
      const blob = new Blob([blobPart], { type: 'audio/mp3' });
      this.audio.src = URL.createObjectURL(blob);
      this.eventManager.triggerEventHook("RAGAI.AUDIO.SPEECH.play", { text: input, voice });
      await this.audio.play().catch((e: unknown) => {
        console.error("Failed to play audio TTSNEW", e);
      });
      this.currentPlayingHash = hash;
    }
  }

  public stopAudio() {
    this.audio.pause();
    this.currentPlayingHash = "";
    this.audioState.next(["idle", ""]);
    this.state = "idle";
  }

  public async playDemoAudio(input: string, url: string) {
    if (this.lastPlayedItem.text === input && Date.now() - this.lastPlayedItem.time < 500) return;
    this.lastPlayedItem = { text: input, time: Date.now() };
    const hash = this.storeHashFromText(input);

    if (this.state === "play") {
      this.audio.pause();
      this.audioState.next(["idle", ""]);
      this.state = "idle";
    }

    this.audioState.next(["load", hash]);
    this.state = "load";

    if (this.audioDict[hash]?.status === "created") {
      this.audioDict[hash].status = "loaded";
    }

    if (this.state !== "idle") {
      this.audioState.next(["play", hash]);
      this.state = "play";
      this.audio.src = url;
      await this.audio.play().catch((e: unknown) => {
        console.error("Failed to play audio TTSNEW", e);
      });
      this.currentPlayingHash = hash;
    }
  }
}
